// Import Supabase client
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

import { useAuthStore } from '@/stores/auth'

export interface ReconciliationFilters {
  dateFrom: string | null
  dateTo: string | null
  tankIds: string[]
  varianceThreshold: number
}

export interface PumpData {
  pump_id: string
  pump_name: string
  opening_reading: number
  closing_reading: number
  dispensed: number
  manual_dispensed: number
  variance: number
}

export interface DeliveryStatistics {
  total_deliveries: number
  total_quantity: number
  total_cost: number
  average_cost_per_liter: number
  suppliers: string[]
  first_delivery_date: string | null
  last_delivery_date: string | null
  delivery_frequency_days: number
}

export interface ApiReading {
  id: number
  tank_id: string
  reading_timestamp: string
  volume_litres: number
  ullage_litres: number
  created_at: string
}

export interface RefuelingRecord {
  id: string
  vehicle_id: string
  created_at: string
  fuel_type: string
  quantity: number
  cost_per_unit: number | null
  total_cost: number | null
  before_odometer_reading: number
  after_odometer_reading: number | null
  location: string | null
  filled_by: string | null
  notes: string | null
  card_refuel: boolean | null
  driver_id: string | null
  tank_id: string | null
  tank_name: string | null
  driver_name: string | null
  vehicle_reg: string | null
  vehicle_name: string | null
  depot_name: string | null
  pump_read_before: number | null
  pump_read_after: number | null
  total_liters: number | null
  pump_id: string | null
  pump_name: string | null
  voucher_reference: string | null
  fleet_number?: string | null
  vehicles?: {
    fleet_number?: string | null
  } | {
    fleet_number?: string | null
  }[] | null
  subconfleet?: {
    fleet_number?: string | null
  } | {
    fleet_number?: string | null
  }[] | null
}

export interface RefuelingStatistics {
  total_refuels: number
  total_quantity: number
  total_cost: number
  average_cost_per_liter: number
  unique_vehicles: number
  unique_drivers: number
  card_refuels: number
  cash_refuels: number
  fuel_types: string[]
  fuel_consumption: {
    average_km_per_liter: number
    average_liters_per_100km: number
    total_distance: number
    efficiency_rating: string
  }
  date_range: {
    from: string | null
    to: string | null
  }
}

export interface TankReconciliationData {
  tank_id: string
  tank_name: string
  fuel_type: string
  depot_name: string
  capacity: number
  current_level: number
  last_api_level: number
  last_physical_dip_level: number
  last_api_read_at: string
  last_physical_dip_at: string
  atg_device_id: string
  atg_imei: string
  opening_balance: number
  opening_balance_source: string
  deliveries: number
  pump_dispensed: number
  closing_balance: number
  closing_balance_source: string
  variance: number
  variance_percentage: number
  status: 'acceptable' | 'investigate' | 'critical'
  last_reconciliation: string
  pumps: PumpData[]
  delivery_history: any[]
  delivery_statistics: DeliveryStatistics
  api_readings: ApiReading[]
  latest_api_reading: ApiReading | null
  api_reading_count: number
  refueling_records: RefuelingRecord[]
  refueling_statistics: RefuelingStatistics
  fuel_consumption: {
    average_km_per_liter: number
    average_liters_per_100km: number
    total_distance: number
    efficiency_rating: string
  }
  opening_rate?: number
  closing_rate?: number
}

export interface ReconciliationSummary {
  total_tanks: number
  acceptable_variances: number
  investigate_variances: number
  total_deliveries: number
  total_dispensed: number
  average_variance_percentage: number
}

class TankReconciliationService {
  // Copy API integration functions from Dashboard
  private async fetchLatestApiReadings(): Promise<Record<string, any>> {
    try {
      const { data, error } = await supabase
        .from('fuel_tank_api_readings')
        .select('tank_id, volume_litres, ullage_litres, reading_timestamp')
        .order('reading_timestamp', { ascending: false })

      if (!error && data) {
        const readingsByTank: Record<string, any> = {}
        data.forEach(reading => {
          if (!readingsByTank[reading.tank_id]) {
            readingsByTank[reading.tank_id] = {
              volume: reading.volume_litres,
              ullage: reading.ullage_litres,
              ts: reading.reading_timestamp
            }
          }
        })
        return readingsByTank
      }
    } catch (err) {
      console.error('Error fetching latest API readings:', err)
    }
    return {}
  }

  // Refresh API readings for all tanks (similar to Dashboard)
  async refreshTankApiReadings(): Promise<void> {
    try {
      console.log('🔄 Refreshing tank API readings...')

      // Get tank API credentials
      const { data: credentials, error: credError } = await supabase
        .from('tank_api_credentials')
        .select('*')
        .eq('status', 'active')

      if (credError) {
        console.error('❌ Error fetching credentials:', credError)
        return
      }

      if (!credentials || credentials.length === 0) {
        console.log('⚠️ No active tank API credentials found')
        return
      }

      // Fetch fresh data from APIs for each active credential
      for (const cred of credentials) {
        try {
          await this.fetchLatestForCredential(cred)
          // Small delay between API calls
          await new Promise(resolve => setTimeout(resolve, 1000))
        } catch (error) {
          console.error(`❌ Failed to refresh ${cred.device_id}:`, error)
        }
      }

      console.log('✅ Tank API readings refresh completed')
    } catch (error) {
      console.error('❌ Error during tank API refresh:', error)
    }
  }

  private async fetchLatestForCredential(cred: any) {
    try {
      const token = await this.fiLogin(cred.base_url, cred.username, cred.password)
      const json = await this.fiGetLatestTimeseries(cred.base_url, token, 'DEVICE', cred.device_id, 'ullage,volume')

      const volArr = Array.isArray(json?.volume) ? json.volume : []
      const ullArr = Array.isArray(json?.ullage) ? json.ullage : []
      const vol = volArr[0]?.value != null ? Number(volArr[0].value) : null
      const ts = (volArr[0]?.ts ?? ullArr[0]?.ts) ?? null
      const ull = ullArr[0]?.value != null ? Number(ullArr[0].value) : null

      // Save to Supabase if data is available
      if (vol !== null && ull !== null && ts !== null) {
        const readingTimestamp = new Date(ts).toISOString()
        const { error } = await supabase.from('fuel_tank_api_readings').insert({
          tank_id: cred.tank_id,
          reading_timestamp: readingTimestamp,
          volume_litres: vol,
          ullage_litres: ull
        })
        if (error) {
          console.error('❌ Failed to save reading:', error)
        } else {
          console.log(`✅ Saved API reading for tank ${cred.tank_id}: ${vol}L`)
        }
      }
    } catch (e: any) {
      console.error('❌ Error fetching credential data:', e?.message || String(e))
    }
  }

  private async fiLogin(baseUrl: string, username: string, password: string): Promise<string> {
    const base = this.apiBase(baseUrl)
    const resp = await fetch(`${base.replace(/\/$/, '')}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ username, password })
    })
    if (!resp.ok) {
      const text = await resp.text()
      throw new Error(`Login failed (${resp.status}): ${text}`)
    }
    const data = await resp.json()
    const token = (data && (data.token || data.jwt || data.accessToken)) as string
    if (!token) throw new Error('No token in login response')
    return token
  }

  private async fiGetLatestTimeseries(baseUrl: string, token: string, entityType: string, entityId: string, keys: string) {
    const base = this.apiBase(baseUrl)
    const url = `${base.replace(/\/$/, '')}/api/plugins/telemetry/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/values/timeseries?keys=${encodeURIComponent(keys)}`
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Authorization': `Bearer ${token}`,
        'Authorization': `Bearer ${token}`
      }
    })
    if (!resp.ok) {
      const text = await resp.text()
      throw new Error(`Timeseries failed (${resp.status}): ${text}`)
    }
    return resp.json()
  }

  private apiBase(raw: string): string {
    const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    if (isDev) return '/fi'
    return this.sanitizeBaseUrl(raw)
  }

  private sanitizeBaseUrl(raw: string): string {
    try {
      const u = new URL(raw)
      return u.origin
    } catch {
      return raw.replace(/^(https?:\/\/[^/]+).*$/, '$1')
    }
  }

  async getReconciliationData(filters: ReconciliationFilters): Promise<{
    data: TankReconciliationData[]
    summary: ReconciliationSummary
  }> {
    // Log the received filters for debugging
    console.log('🔍 getReconciliationData received filters:', {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      tankIds: filters.tankIds,
      varianceThreshold: filters.varianceThreshold,
      dateFromType: typeof filters.dateFrom,
      dateToType: typeof filters.dateTo
    })
    try {
      // Build tank filter query with enhanced current level debugging
      let tankQuery = supabase
        .from('fuel_tanks')
        .select(`
          id,
          name,
          fuel_type,
          depot_name,
          capacity,
          current_level,
          last_api_level,
          last_physical_dip_level,
          last_api_read_at,
          last_physical_dip_at,
          atg_device_id,
          atg_imei,
          status,
          notes,
          created_at,
          updated_at,
          office_id
        `)
        .eq('status', 'active')
        .order('name')

      // Apply tank filter if specified
      if (filters.tankIds && filters.tankIds.length > 0) {
        tankQuery = tankQuery.in('id', filters.tankIds)
      }

      // Restrict by user scope
      const authStore = useAuthStore()
      const currentUserRole = authStore.user?.user_metadata?.role || ''
      const isHeadOffice = authStore.hasGlobalAccess

      if (!isHeadOffice) {
        let currentUserCompany = authStore.user?.user_metadata?.company || ''
        if (!currentUserCompany && authStore.user?.id) {
            const { data: profile } = await supabase.from('user_profiles').select('company').eq('user_id', authStore.user.id).single()
            if (profile && profile.company) currentUserCompany = profile.company
        }
        const currentOfficeIds = currentUserCompany ? currentUserCompany.split(',').map((id: string) => id.trim()).filter(Boolean) : []
        if (currentOfficeIds.length > 0) {
           tankQuery = tankQuery.in('office_id', currentOfficeIds)
        } else {
           // User has no offices, return nothing
           return { data: [], summary: { total_tanks: 0, acceptable_variances: 0, investigate_variances: 0, total_deliveries: 0, total_dispensed: 0, average_variance_percentage: 0 } }
        }
      }

      const { data: tanks, error: tankError } = await tankQuery
      if (tankError) {
        console.error('❌ Error fetching tanks:', tankError)
        return this.getFallbackData(filters)
      }

      // Debug current tank levels
      console.log('🔍 Tank levels debugging:')
      tanks?.forEach(tank => {
        console.log(`Tank: ${tank.name}`, {
          current_level: tank.current_level,
          last_api_level: tank.last_api_level,
          last_physical_dip_level: tank.last_physical_dip_level,
          last_api_read_at: tank.last_api_read_at,
          last_physical_dip_at: tank.last_physical_dip_at,
          capacity: tank.capacity
        })
      })

      if (!tanks || tanks.length === 0) {
        return {
          data: [],
          summary: {
            total_tanks: 0,
            acceptable_variances: 0,
            investigate_variances: 0,
            total_deliveries: 0,
            total_dispensed: 0,
            average_variance_percentage: 0
          }
        }
      }

      // Fetch reconciliation data for each tank
      const reconciliationData: TankReconciliationData[] = []

      for (const tank of tanks) {
        const tankData = await this.getTankReconciliationData(tank, filters)
        if (tankData) {
          reconciliationData.push(tankData)
        }
      }

      // Calculate summary
      const summary = this.calculateSummary(reconciliationData, filters)

      return {
        data: reconciliationData,
        summary
      }
    } catch (error) {
      console.error('Error in getReconciliationData:', error)
      return this.getFallbackData(filters)
    }
  }

  private async getTankReconciliationData(tank: any, filters: ReconciliationFilters): Promise<TankReconciliationData | null> {
    // Log the date filters to help with debugging
    console.log(`🔍 Processing tank ${tank.name} with date filters:`, {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      type: typeof filters.dateFrom
    })
    try {
      // Get API readings for the selected time period
      let apiQuery = supabase
        .from('fuel_tank_api_readings')
        .select('*')
        .eq('tank_id', tank.id)
        .order('reading_timestamp', { ascending: false })

      // Apply date filters if provided
      if (filters.dateFrom) {
        apiQuery = apiQuery.gte('reading_timestamp', filters.dateFrom)
      }

      if (filters.dateTo) {
        apiQuery = apiQuery.lte('reading_timestamp', filters.dateTo)
      }

      const { data: apiReadings, error: apiError } = await apiQuery

      if (apiError) {
        console.error(`❌ Error fetching API readings for ${tank.name}:`, apiError)
      } else {
        console.log(`📡 Found ${apiReadings?.length || 0} API readings for ${tank.name} in selected period`)
      }

      // Get latest API reading (most recent overall, not just in date range)
      const { data: latestReading, error: latestError } = await supabase
        .from('fuel_tank_api_readings')
        .select('*')
        .eq('tank_id', tank.id)
        .order('reading_timestamp', { ascending: false })
        .limit(1)

      if (latestError) {
        console.error(`❌ Error fetching latest API reading for ${tank.name}:`, latestError)
      } else {
        console.log(`📡 Latest API reading for ${tank.name}:`, latestReading?.[0])
      }

      // Get refueling records for the selected time period
      console.log(`🔍 Fetching refueling records for tank ${tank.name} (${tank.id}) from ${filters.dateFrom} to ${filters.dateTo}`)

      // Get refueling records for the selected time period
      let refuelingQuery = supabase
        .from('fuel_records')
        .select(`
          id, vehicle_id, created_at, fuel_type, quantity, cost_per_unit, total_cost,
          before_odometer_reading, after_odometer_reading, location, filled_by,
          notes, card_refuel, driver_id, tank_id, tank_name, driver_name,
          vehicle_reg, vehicle_name, depot_name, pump_read_before, pump_read_after,
          total_liters, pump_id, voucher_reference,
          vehicles(fleet_number),
          subconfleet(fleet_number)
        `)
        .eq('tank_id', tank.id)
        .order('created_at', { ascending: false })

      // Apply date filters if provided
      if (filters.dateFrom) {
        refuelingQuery = refuelingQuery.gte('created_at', filters.dateFrom)
      }

      if (filters.dateTo) {
        refuelingQuery = refuelingQuery.lte('created_at', filters.dateTo)
      }

      const { data: refuelingRecords, error: refuelingError } = await refuelingQuery

      if (refuelingError) {
        console.error(`❌ Error fetching refueling records for ${tank.name}:`, refuelingError)
      } else {
        console.log(`⛽ Found ${refuelingRecords?.length || 0} refueling records for ${tank.name} in selected period`)
        console.log('📊 Refueling records sample:', refuelingRecords?.slice(0, 2))
      }

      // Get pump names separately if needed
      let pumpNames: Record<string, string> = {}
      if (refuelingRecords && refuelingRecords.length > 0) {
        const pumpIds = [...new Set(refuelingRecords.map(r => r.pump_id).filter(Boolean))]
        if (pumpIds.length > 0) {
          const { data: pumps } = await supabase
            .from('pumps')
            .select('id, name')
            .in('id', pumpIds)

          if (pumps) {
            pumpNames = pumps.reduce((acc, pump) => {
              acc[pump.id] = pump.name
              return acc
            }, {} as Record<string, string>)
          }
        }
      }

      // Validate pump meter readings vs quantity
      const pumpValidation = this.validatePumpMeterReadings(refuelingRecords || [])
      console.log(`🔍 Pump meter validation for ${tank.name}:`, pumpValidation)

      // Calculate refueling statistics
      const refuelingStats = this.calculateRefuelingStatistics(refuelingRecords || [], filters)
      // Get deliveries in date range for reconciliation period
      const { data: deliveries } = await supabase
        .from('tank_refills')
        .select('quantity, created_at, supplier, dip_before_refill, dip_after_refill, invoice_number, total_cost, cost_per_unit, attendant_id, notes')
        .eq('tank_id', tank.id)
        .gte('created_at', filters.dateFrom)
        .lte('created_at', filters.dateTo)
        .order('created_at')

      // Get deliveries within the selected date range for delivery history
      const { data: allDeliveries, error: deliveryError } = await supabase
        .from('tank_refills')
        .select(`
          id,
          created_at,
          quantity,
          supplier,
          invoice_number,
          total_cost,
          cost_per_unit,
          dip_before_refill,
          dip_after_refill,
          dip_difference,
          attendant_id,
          notes
        `)
        .eq('tank_id', tank.id)
        .gte('created_at', filters.dateFrom)
        .lte('created_at', filters.dateTo)
        .order('created_at', { ascending: false })

      if (deliveryError) {
        console.error(`❌ Error fetching all deliveries for ${tank.name}:`, deliveryError)
      } else {
        console.log(`📦 Found ${allDeliveries?.length || 0} deliveries for ${tank.name} in date range ${filters.dateFrom} to ${filters.dateTo}`)
      }

      // Calculate comprehensive delivery statistics
      const deliveryStats = this.calculateDeliveryStatistics(allDeliveries || [])

      // Get pump dispensing data in date range with pump details
      console.log(`🔍 Fetching pump data for tank: ${tank.name} (${tank.id})`)

      // Primary approach: Use fuel_records_with_pump view
      let { data: pumpData, error: pumpError } = await supabase
        .from('fuel_records_with_pump')
        .select(`
          total_liters,
          pump_id,
          pump_name,
          pump_read_before,
          pump_read_after,
          created_at,
          associated_tank_name,
          tank_name,
          quantity
        `)
        .eq('tank_id', tank.id)
        .gte('created_at', filters.dateFrom)
        .lte('created_at', filters.dateTo)
        .order('created_at')

      // Fallback: Try with tank name matching if tank_id didn't work
      if (!pumpData || pumpData.length === 0) {
        console.log(`🔄 No pump data found with tank_id, trying with tank name '${tank.name}'...`)
        const { data: pumpDataByName, error: pumpErrorByName } = await supabase
          .from('fuel_records_with_pump')
          .select(`
            total_liters,
            pump_id,
            pump_name,
            pump_read_before,
            pump_read_after,
            created_at,
            associated_tank_name,
            tank_name,
            quantity
          `)
          .or(`tank_name.eq.${tank.name},associated_tank_name.eq.${tank.name}`)
          .gte('created_at', filters.dateFrom)
          .lte('created_at', filters.dateTo)
          .order('created_at')

        if (pumpDataByName && pumpDataByName.length > 0) {
          pumpData = pumpDataByName
          pumpError = pumpErrorByName
        }
      }

      // Final fallback: Direct fuel_records table with pump join
      if (!pumpData || pumpData.length === 0) {
        console.log(`🔄 Trying direct fuel_records table with pump join...`)
        const { data: pumpDataDirect, error: pumpErrorDirect } = await supabase
          .from('fuel_records')
          .select(`
            total_liters,
            pump_id,
            pump_read_before,
            pump_read_after,
            created_at,
            quantity,
            pumps!inner(id, name, tank_id)
          `)
          .eq('tank_id', tank.id)
          .gte('created_at', filters.dateFrom)
          .lte('created_at', filters.dateTo)
          .order('created_at')

        if (pumpDataDirect && pumpDataDirect.length > 0) {
          pumpData = pumpDataDirect.map((record: any) => ({
            ...record,
            pump_name: record.pumps?.name || `Pump ${record.pump_id}`,
            associated_tank_name: tank.name,
            tank_name: tank.name
          }))
          pumpError = pumpErrorDirect
        }
      }

      if (pumpError) {
        console.error(`❌ Error fetching pump data for ${tank.name}:`, pumpError)
      } else {
        console.log(`✅ Found ${pumpData?.length || 0} pump records for ${tank.name}`)
        if (tank.name.includes('VB-DT8') || tank.name.includes('VDBijl')) {
          console.log(`🎯 VB-DT8 VDBijl Park pump data:`, pumpData)
        }
      }

      // Get individual pump breakdown
      const pumpBreakdown = await this.getPumpBreakdown(tank.id, pumpData || [])

      // Get latest reconciliation
      const { data: lastRecon } = await supabase
        .from('tank_reconciliations')
        .select('*')
        .eq('tank_id', tank.id)
        .order('recon_date', { ascending: false })
        .limit(1)

      // Calculate totals with better error handling
      const totalDeliveries = deliveries?.reduce((sum, d) => {
        const quantity = parseFloat(d.quantity) || 0
        return sum + quantity
      }, 0) || 0

      const totalDispensed = pumpBreakdown.reduce((sum, p) => sum + p.dispensed, 0)

      // 1. Get Opening Balance (Reading closest to dateFrom - ON OR AFTER the start date)
      let openingBalance = 0
      let openingSource = 'Estimated (Capacity)'

      // Try to get API reading at or after start date (changed from lte to gte)
      const { data: openingApiReadings } = await supabase
        .from('fuel_tank_api_readings')
        .select('*')
        .eq('tank_id', tank.id)
        .gte('reading_timestamp', filters.dateFrom) // Changed from lte to gte - get readings ON OR AFTER start date
        .order('reading_timestamp', { ascending: true }) // Changed from false to true - get earliest reading first
        .limit(1)

      if (openingApiReadings && openingApiReadings.length > 0) {
        openingBalance = parseFloat(openingApiReadings[0].volume_litres) || 0
        openingSource = `API Reading (${new Date(openingApiReadings[0].reading_timestamp).toLocaleString()})`
      } else {
        // Fallback to previous reconciliation if no API reading found on or after date
        // Or could try physical dips here
        if (lastRecon?.[0]?.dip_reading) {
          openingBalance = parseFloat(lastRecon[0].dip_reading) || 0
          openingSource = `Previous Reconciliation (${new Date(lastRecon[0].recon_date).toLocaleDateString()})`
        } else if (tank.capacity) {
          // Absolute fallback
          openingBalance = parseFloat(tank.capacity) * 0.5
          openingSource = 'Estimated (50% Capacity - No Data)'
        }
      }

      // 2. Get Closing Balance (Reading closest to dateTo)
      let actualBalance = 0
      let closingSource = 'Estimated (Capacity)'

      // Try to get the latest API reading on or before the end date
      const { data: closingApiReadings } = await supabase
        .from('fuel_tank_api_readings')
        .select('*')
        .eq('tank_id', tank.id)
        .lte('reading_timestamp', filters.dateTo)
        .order('reading_timestamp', { ascending: false }) // Last reading first
        .limit(1)

      if (closingApiReadings && closingApiReadings.length > 0) {
        actualBalance = parseFloat(closingApiReadings[0].volume_litres) || 0
        closingSource = `API Reading (${new Date(closingApiReadings[0].reading_timestamp).toLocaleString()})`
      } else {
        // Fallback to current level if no API data is found for the period
        if (tank.current_level) {
          actualBalance = parseFloat(tank.current_level) || 0
          closingSource = 'Current Level (No API Data for Period)'
        }
      }

      console.log(`🔧 Tank ${tank.name} balance calculation for period ${filters.dateFrom} - ${filters.dateTo}:`, {
        openingRaw: openingApiReadings?.[0],
        closingRaw: closingApiReadings?.[0],
        openingBalance,
        openingSource,
        actualBalance,
        closingSource
      })

      // Calculate expected closing balance
      const calculatedBalance = openingBalance + totalDeliveries - totalDispensed

      // Calculate variance
      const variance = actualBalance - calculatedBalance
      const variancePercentage = calculatedBalance > 0 ? Math.abs(variance / calculatedBalance) * 100 : 0

      let openingRate = 0;

      // 1. Try to get exact opening rate from active contracts for this date
      if (filters.dateFrom) {
        const { data: openingContract } = await supabase
          .from('contracts')
          .select('subcontract_price, price')
          .eq('fuel_tank_id', tank.id)
          .lte('start_date', filters.dateFrom)
          .or(`end_date.gte.${filters.dateFrom},end_date.is.null`)
          .order('start_date', { ascending: false })
          .limit(1);

        if (openingContract && openingContract.length > 0) {
          openingRate = Number(openingContract[0].subcontract_price) || Number(openingContract[0].price) || 0;
        }
      }

      // 2. Fallback to exact opening delivery if no contract exists
      if (!openingRate) {
        const { data: openingDelivery } = await supabase
          .from('tank_refills')
          .select('cost_per_unit, created_at')
          .eq('tank_id', tank.id)
          .lte('created_at', filters.dateFrom)
          .order('created_at', { ascending: false })
          .limit(1)
          
        if (openingDelivery && openingDelivery.length > 0) {
          openingRate = parseFloat(openingDelivery[0].cost_per_unit) || 0;
          if (filters.dateFrom) {
            const deliveryDate = new Date(openingDelivery[0].created_at);
            const fromDate = new Date(filters.dateFrom);
            const daysOld = (fromDate.getTime() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysOld > 60 && deliveryStats.average_cost_per_liter > 0) {
               openingRate = deliveryStats.average_cost_per_liter;
            }
          }
        }
      }

      let closingRate = 0;
      let closingContractFound = false;

      // 1. Try to get exact closing rate from active contracts for this date
      if (filters.dateTo) {
        const { data: closingContract } = await supabase
          .from('contracts')
          .select('subcontract_price, price')
          .eq('fuel_tank_id', tank.id)
          .lte('start_date', filters.dateTo)
          .or(`end_date.gte.${filters.dateTo},end_date.is.null`)
          .order('start_date', { ascending: false })
          .limit(1);

        if (closingContract && closingContract.length > 0) {
          const rate = Number(closingContract[0].subcontract_price) || Number(closingContract[0].price) || 0;
          if (rate > 0) {
            closingRate = rate;
            closingContractFound = true;
          }
        }
      }

      // 2. Fallback to exact closing delivery if no contract exists
      if (!closingContractFound) {
        closingRate = openingRate;
        const { data: closingDelivery } = await supabase
          .from('tank_refills')
          .select('cost_per_unit, created_at')
          .eq('tank_id', tank.id)
          .lte('created_at', filters.dateTo)
          .order('created_at', { ascending: false })
          .limit(1)
          
        if (closingDelivery && closingDelivery.length > 0) {
          const fetchedClosingRate = parseFloat(closingDelivery[0].cost_per_unit) || 0;
          if (fetchedClosingRate > 0) {
             closingRate = fetchedClosingRate;
          }
          if (filters.dateTo) {
            const deliveryDate = new Date(closingDelivery[0].created_at);
            const toDate = new Date(filters.dateTo);
            const daysOld = (toDate.getTime() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysOld > 60 && deliveryStats.average_cost_per_liter > 0) {
               closingRate = deliveryStats.average_cost_per_liter;
            }
          }
        }
      }

      console.log(`📊 Tank ${tank.name} calculations:`, {
        openingBalance,
        totalDeliveries,
        totalDispensed,
        calculatedBalance,
        actualBalance,
        variance,
        variancePercentage,
        openingRate,
        closingRate
      })

      return {
        tank_id: tank.id,
        tank_name: tank.name,
        fuel_type: tank.fuel_type || 'Diesel',
        depot_name: tank.depot_name || tank.name,
        capacity: tank.capacity || 0,
        current_level: tank.current_level || 0,
        last_api_level: tank.last_api_level || 0,
        last_physical_dip_level: tank.last_physical_dip_level || 0,
        last_api_read_at: tank.last_api_read_at || new Date().toISOString(),
        last_physical_dip_at: tank.last_physical_dip_at || new Date().toISOString(),
        atg_device_id: tank.atg_device_id || '',
        atg_imei: tank.atg_imei || '',
        opening_balance: openingBalance,
        opening_balance_source: openingSource,
        opening_rate: openingRate || deliveryStats.average_cost_per_liter || 0,
        deliveries: totalDeliveries,
        pump_dispensed: totalDispensed,
        closing_balance: actualBalance,
        closing_balance_source: closingSource,
        closing_rate: closingRate || deliveryStats.average_cost_per_liter || 0,
        variance: variance,
        variance_percentage: variancePercentage,
        status: variancePercentage <= filters.varianceThreshold ? 'acceptable' : 'investigate',
        last_reconciliation: lastRecon?.[0]?.recon_date || null,
        pumps: pumpBreakdown,
        delivery_history: allDeliveries || [],
        delivery_statistics: deliveryStats,
        api_readings: apiReadings || [],
        latest_api_reading: latestReading?.[0] || null,
        api_reading_count: apiReadings?.length || 0,
        refueling_records: (refuelingRecords || []).map(record => {
          // Extract fleet_number from nested objects if available
          let fleetNumber = null;

          // Check vehicles - using type assertions to handle TypeScript errors
          if (record.vehicles) {
            if (Array.isArray(record.vehicles) && record.vehicles.length > 0) {
              // Type assertion to access array element
              const firstVehicle = record.vehicles[0] as any;
              fleetNumber = firstVehicle.fleet_number || null;
            } else if (typeof record.vehicles === 'object') {
              // Type assertion to access object property
              const vehiclesObj = record.vehicles as any;
              fleetNumber = vehiclesObj.fleet_number || null;
            }
          }

          // Check subconfleet if no fleet_number found yet - using type assertions
          if (!fleetNumber && record.subconfleet) {
            if (Array.isArray(record.subconfleet) && record.subconfleet.length > 0) {
              // Type assertion to access array element
              const firstSubcon = record.subconfleet[0] as any;
              fleetNumber = firstSubcon.fleet_number || null;
            } else if (typeof record.subconfleet === 'object') {
              // Type assertion to access object property
              const subconObj = record.subconfleet as any;
              fleetNumber = subconObj.fleet_number || null;
            }
          }

          return {
            ...record,
            pump_name: pumpNames[record.pump_id] || null,
            fleet_number: fleetNumber
          };
        }),
        refueling_statistics: refuelingStats,
        fuel_consumption: refuelingStats.fuel_consumption
      }
    } catch (error) {
      console.error('Error getting tank reconciliation data:', error)
      return null
    }
  }

  private calculateSummary(data: TankReconciliationData[], filters: ReconciliationFilters): ReconciliationSummary {
    const acceptable = data.filter(t => t.status === 'acceptable').length
    const investigate = data.filter(t => t.status === 'investigate').length
    const totalDeliveries = data.reduce((sum, tank) => sum + tank.deliveries, 0)
    const totalDispensed = data.reduce((sum, tank) => sum + tank.pump_dispensed, 0)
    const avgVariance = data.length > 0 ? data.reduce((sum, tank) => sum + tank.variance_percentage, 0) / data.length : 0

    return {
      total_tanks: data.length,
      acceptable_variances: acceptable,
      investigate_variances: investigate,
      total_deliveries: totalDeliveries,
      total_dispensed: totalDispensed,
      average_variance_percentage: avgVariance
    }
  }

  private calculateRefuelingStatistics(records: any[], filters: ReconciliationFilters): RefuelingStatistics {
    if (!records || records.length === 0) {
      return {
        total_refuels: 0,
        total_quantity: 0,
        total_cost: 0,
        average_cost_per_liter: 0,
        unique_vehicles: 0,
        unique_drivers: 0,
        card_refuels: 0,
        cash_refuels: 0,
        fuel_types: [],
        fuel_consumption: {
          average_km_per_liter: 0,
          average_liters_per_100km: 0,
          total_distance: 0,
          efficiency_rating: 'N/A'
        },
        date_range: {
          from: filters.dateFrom,
          to: filters.dateTo
        }
      }
    }

    const totalQuantity = records.reduce((sum, r) => sum + (r.quantity || 0), 0)
    const totalCost = records.reduce((sum, r) => sum + (r.total_cost || 0), 0)
    const uniqueVehicles = new Set(records.map(r => r.vehicle_id).filter(Boolean)).size
    const uniqueDrivers = new Set(records.map(r => r.driver_id).filter(Boolean)).size
    const cardRefuels = records.filter(r => r.card_refuel === true).length
    const cashRefuels = records.filter(r => r.card_refuel === false).length
    const fuelTypes = [...new Set(records.map(r => r.fuel_type).filter(Boolean))]

    // Calculate fuel consumption metrics
    const fuelConsumption = this.calculateFuelConsumption(records)

    return {
      total_refuels: records.length,
      total_quantity: totalQuantity,
      total_cost: totalCost,
      average_cost_per_liter: totalQuantity > 0 ? totalCost / totalQuantity : 0,
      unique_vehicles: uniqueVehicles,
      unique_drivers: uniqueDrivers,
      card_refuels: cardRefuels,
      cash_refuels: cashRefuels,
      fuel_types: fuelTypes,
      fuel_consumption: fuelConsumption,
      date_range: {
        from: filters.dateFrom,
        to: filters.dateTo
      }
    }
  }

  private calculateFuelConsumption(records: any[]): {
    average_km_per_liter: number
    average_liters_per_100km: number
    total_distance: number
    efficiency_rating: string
  } {
    // NOTE: before_odometer_reading and after_odometer_reading are actually PUMP METER readings, not vehicle odometer
    // For fuel consumption calculations, we need actual vehicle odometer data (not available in current schema)
    // This function currently returns N/A until proper vehicle odometer fields are available

    console.warn('⚠️ Fuel consumption calculation disabled: before/after_odometer_reading fields contain pump meter readings, not vehicle odometer data')

    return {
      average_km_per_liter: 0,
      average_liters_per_100km: 0,
      total_distance: 0,
      efficiency_rating: 'N/A - Missing Vehicle Odometer Data'
    }
  }

  private validatePumpMeterReadings(records: any[]): {
    valid_records: number
    invalid_records: number
    discrepancies: any[]
  } {
    // Validate that pump meter difference equals quantity
    const discrepancies: any[] = []
    let validCount = 0
    let invalidCount = 0

    records.forEach(record => {
      if (record.before_odometer_reading && record.after_odometer_reading && record.quantity) {
        const pumpDifference = record.after_odometer_reading - record.before_odometer_reading
        const quantityDifference = Math.abs(pumpDifference - record.quantity)

        // Allow small tolerance for rounding (0.01L)
        if (quantityDifference > 0.01) {
          invalidCount++
          discrepancies.push({
            record_id: record.id,
            vehicle_reg: record.vehicle_reg,
            created_at: record.created_at,
            pump_difference: pumpDifference,
            recorded_quantity: record.quantity,
            discrepancy: quantityDifference,
            pump_before: record.before_odometer_reading,
            pump_after: record.after_odometer_reading
          })
        } else {
          validCount++
        }
      }
    })

    if (discrepancies.length > 0) {
      console.warn('🚨 Pump meter discrepancies found:', discrepancies)
    }

    return {
      valid_records: validCount,
      invalid_records: invalidCount,
      discrepancies
    }
  }

  private calculateDeliveryStatistics(deliveries: any[]): DeliveryStatistics {
    if (!deliveries || deliveries.length === 0) {
      return {
        total_deliveries: 0,
        total_quantity: 0,
        total_cost: 0,
        average_cost_per_liter: 0,
        suppliers: [],
        first_delivery_date: null,
        last_delivery_date: null,
        delivery_frequency_days: 0
      }
    }

    const totalQuantity = deliveries.reduce((sum, d) => sum + (d.quantity || 0), 0)
    const totalCost = deliveries.reduce((sum, d) => sum + (d.total_cost || 0), 0)
    const uniqueSuppliers = [...new Set(deliveries.map(d => d.supplier).filter(Boolean))]

    // Sort deliveries by date for frequency calculation
    const sortedDeliveries = deliveries
      .filter(d => d.created_at)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

    const firstDate = sortedDeliveries.length > 0 ? sortedDeliveries[0].created_at : null
    const lastDate = sortedDeliveries.length > 0 ? sortedDeliveries[sortedDeliveries.length - 1].created_at : null

    // Calculate average delivery frequency in days
    let frequencyDays = 0
    if (sortedDeliveries.length > 1 && firstDate && lastDate) {
      const daysDiff = Math.abs(new Date(lastDate).getTime() - new Date(firstDate).getTime()) / (1000 * 60 * 60 * 24)
      frequencyDays = Math.round(daysDiff / (sortedDeliveries.length - 1))
    }

    return {
      total_deliveries: deliveries.length,
      total_quantity: totalQuantity,
      total_cost: totalCost,
      average_cost_per_liter: totalQuantity > 0 ? totalCost / totalQuantity : 0,
      suppliers: uniqueSuppliers,
      first_delivery_date: firstDate,
      last_delivery_date: lastDate,
      delivery_frequency_days: frequencyDays
    }
  }

  private async getPumpBreakdown(tankId: string, pumpData: any[]): Promise<PumpData[]> {
    if (!pumpData || pumpData.length === 0) {
      console.log(`⚠️ No pump data available for tank ${tankId}`)
      return []
    }

    // Group pump data by pump_id and calculate totals
    const pumpMap = new Map<string, any>()

    pumpData.forEach(record => {
      if (!record.pump_id) {
        console.warn(`⚠️ Pump record missing pump_id:`, record)
        return
      }

      const pumpId = record.pump_id
      const pumpName = record.pump_name || `Pump ${pumpId}`

      if (!pumpMap.has(pumpId)) {
        pumpMap.set(pumpId, {
          pump_id: pumpId,
          pump_name: pumpName,
          opening_reading: null,
          closing_reading: null,
          dispensed: 0,
          manual_dispensed: 0,
          variance: 0,
          record_count: 0,
          first_reading: null,
          last_reading: null
        })
      }

      const pump = pumpMap.get(pumpId)
      pump.record_count++

      // Track total dispensed (METER PRIORITY - HARDWARE TRUTH)
      let dispensedAmount = 0;
      const after = parseFloat(record.pump_read_after);
      const before = parseFloat(record.pump_read_before);

      if (!isNaN(after) && !isNaN(before) && after >= before) {
        dispensedAmount = after - before;
      } else {
        // Fallback to manual quantity
        dispensedAmount = parseFloat(record.quantity) || 0;
      }
      pump.dispensed += dispensedAmount

      // Track manual dispensed (STRICTLY MANUAL LOGS)
      let manualDispensedAmount = parseFloat(record.quantity) || 0;
      pump.manual_dispensed += manualDispensedAmount

      // Track pump readings for variance calculation (chronological order)
      if (record.pump_read_before !== null && record.pump_read_before !== undefined) {
        // Only set the first reading once (chronologically first)
        if (pump.first_reading === null) {
          pump.first_reading = record.pump_read_before
          pump.opening_reading = record.pump_read_before
        }
      }

      if (record.pump_read_after !== null && record.pump_read_after !== undefined) {
        // Continuously update the last reading (so it ends up being the chronologically last)
        pump.last_reading = record.pump_read_after
        pump.closing_reading = record.pump_read_after
      }
    })

    // Calculate variance for each pump and clean up data
    const pumps = Array.from(pumpMap.values()).map(pump => {
      // Calculate variance if we have both opening and closing readings and it hasn't reset
      if (pump.opening_reading !== null && pump.closing_reading !== null && pump.closing_reading >= pump.opening_reading) {
        const calculatedDispensed = pump.closing_reading - pump.opening_reading
        pump.variance = Math.abs(calculatedDispensed - pump.manual_dispensed)
        // Use the absolute meter difference as the TRUE dispensed amount
        pump.dispensed = calculatedDispensed
      } else {
        // If no pump readings available or meter reset, variance is based on sum of transactions vs manual
        pump.variance = Math.abs(pump.dispensed - pump.manual_dispensed)
        pump.opening_reading = pump.opening_reading || 0
        pump.closing_reading = pump.closing_reading || 0
      }

      // Clean up temporary fields
      delete pump.record_count
      delete pump.first_reading
      delete pump.last_reading

      return pump
    })

    console.log(`📊 Processed ${pumps.length} pumps for tank ${tankId}:`, pumps)
    return pumps
  }

  private getFallbackData(filters: ReconciliationFilters): {
    data: TankReconciliationData[]
    summary: ReconciliationSummary
  } {
    // Fallback to mock data if database queries fail
    const mockTankData: TankReconciliationData[] = [
      {
        tank_id: 'tank-001',
        tank_name: 'Main Depot Tank 1',
        fuel_type: 'Diesel',
        depot_name: 'Main Depot Tank 1',
        capacity: 20000,
        current_level: 11500,
        last_api_level: 11480,
        last_physical_dip_level: 11500,
        last_api_read_at: '2024-08-28T06:00:00Z',
        last_physical_dip_at: '2024-08-28T08:30:00Z',
        atg_device_id: 'ATG-MD-001',
        atg_imei: '860123456789012',
        opening_balance: 15000,
        opening_balance_source: 'ATG Reading',
        deliveries: 5000,
        pump_dispensed: 8500,
        closing_balance: 11500,
        closing_balance_source: 'Physical Dip',
        variance: 0,
        variance_percentage: 0,
        status: 'acceptable',
        last_reconciliation: '2024-08-28T08:30:00Z',
        pumps: [
          {
            pump_id: 'pump-001',
            pump_name: 'Pump 1',
            opening_reading: 125000,
            closing_reading: 129500,
            dispensed: 4500,
            manual_dispensed: 4500,
            variance: 0
          },
          {
            pump_id: 'pump-002',
            pump_name: 'Pump 2',
            opening_reading: 98000,
            closing_reading: 102000,
            dispensed: 4000,
            manual_dispensed: 4000,
            variance: 0
          }
        ],
        delivery_history: [
          {
            id: 'delivery-001',
            created_at: '2024-08-20T08:00:00Z',
            quantity: 5000,
            supplier: 'Shell Energy',
            invoice_number: 'INV-2024-001',
            total_cost: 85000,
            cost_per_unit: 17.00,
            dip_before_refill: 10000,
            dip_after_refill: 15000,
            dip_difference: 5000
          }
        ],
        delivery_statistics: {
          total_deliveries: 1,
          total_quantity: 5000,
          total_cost: 85000,
          average_cost_per_liter: 17.00,
          suppliers: ['Shell Energy'],
          first_delivery_date: '2024-08-20T08:00:00Z',
          last_delivery_date: '2024-08-20T08:00:00Z',
          delivery_frequency_days: 0
        },
        api_readings: [],
        latest_api_reading: null,
        api_reading_count: 0,
        refueling_records: [],
        refueling_statistics: {
          total_refuels: 0,
          total_quantity: 0,
          total_cost: 0,
          average_cost_per_liter: 0,
          unique_vehicles: 0,
          unique_drivers: 0,
          card_refuels: 0,
          cash_refuels: 0,
          fuel_types: [],
          fuel_consumption: {
            average_km_per_liter: 0,
            average_liters_per_100km: 0,
            total_distance: 0,
            efficiency_rating: 'N/A'
          },
          date_range: { from: '', to: '' }
        },
        fuel_consumption: {
          average_km_per_liter: 0,
          average_liters_per_100km: 0,
          total_distance: 0,
          efficiency_rating: 'N/A'
        }
      },
      {
        tank_id: 'tank-002',
        tank_name: 'Main Depot Tank 2',
        fuel_type: 'Diesel',
        depot_name: 'Main Depot Tank 2',
        capacity: 15000,
        current_level: 8750,
        last_api_level: 8720,
        last_physical_dip_level: 8750,
        last_api_read_at: '2024-08-28T06:00:00Z',
        last_physical_dip_at: '2024-08-28T08:30:00Z',
        atg_device_id: 'ATG-MD-002',
        atg_imei: '860123456789013',
        opening_balance: 12000,
        opening_balance_source: 'ATG Reading',
        deliveries: 0,
        pump_dispensed: 3200,
        closing_balance: 8750,
        closing_balance_source: 'ATG Reading',
        variance: 50,
        variance_percentage: 0.57,
        status: 'acceptable',
        last_reconciliation: '2024-08-28T08:30:00Z',
        pumps: [
          {
            pump_id: 'pump-003',
            pump_name: 'Pump 3',
            opening_reading: 145000,
            closing_reading: 148200,
            dispensed: 3200,
            manual_dispensed: 3200,
            variance: 0
          }
        ],
        delivery_history: [],
        delivery_statistics: {
          total_deliveries: 0,
          total_quantity: 0,
          total_cost: 0,
          average_cost_per_liter: 0,
          suppliers: [],
          first_delivery_date: null,
          last_delivery_date: null,
          delivery_frequency_days: 0
        },
        api_readings: [],
        latest_api_reading: null,
        api_reading_count: 0,
        refueling_records: [],
        refueling_statistics: {
          total_refuels: 0,
          total_quantity: 0,
          total_cost: 0,
          average_cost_per_liter: 0,
          unique_vehicles: 0,
          unique_drivers: 0,
          card_refuels: 0,
          cash_refuels: 0,
          fuel_types: [],
          fuel_consumption: {
            average_km_per_liter: 0,
            average_liters_per_100km: 0,
            total_distance: 0,
            efficiency_rating: 'N/A'
          },
          date_range: { from: '', to: '' }
        },
        fuel_consumption: {
          average_km_per_liter: 0,
          average_liters_per_100km: 0,
          total_distance: 0,
          efficiency_rating: 'N/A'
        }
      },
      {
        tank_id: 'tank-003',
        tank_name: 'North Depot Tank 1',
        fuel_type: 'Diesel',
        depot_name: 'North Depot Tank 1',
        capacity: 25000,
        current_level: 10900,
        last_api_level: 10850,
        last_physical_dip_level: 10900,
        last_api_read_at: '2024-08-28T06:00:00Z',
        last_physical_dip_at: '2024-08-28T08:30:00Z',
        atg_device_id: 'ATG-ND-001',
        atg_imei: '860123456789014',
        opening_balance: 8000,
        opening_balance_source: 'Physical Dip',
        deliveries: 10000,
        pump_dispensed: 6800,
        closing_balance: 10900,
        closing_balance_source: 'Physical Dip',
        variance: 300,
        variance_percentage: 2.68,
        status: 'investigate',
        last_reconciliation: '2024-08-28T08:30:00Z',
        pumps: [
          {
            pump_id: 'pump-004',
            pump_name: 'Pump 4',
            opening_reading: 156000,
            closing_reading: 159400,
            dispensed: 3400,
            manual_dispensed: 3400,
            variance: 0
          },
          {
            pump_id: 'pump-005',
            pump_name: 'Pump 5',
            opening_reading: 110000,
            closing_reading: 113400,
            dispensed: 3400,
            manual_dispensed: 3400,
            variance: 0
          }
        ],
        delivery_history: [
          {
            id: 'delivery-002',
            created_at: '2024-08-25T10:00:00Z',
            quantity: 10000,
            supplier: 'BP Energy',
            invoice_number: 'INV-2024-002',
            total_cost: 170000,
            cost_per_unit: 17.00,
            dip_before_refill: 8000,
            dip_after_refill: 18000,
            dip_difference: 10000
          }
        ],
        delivery_statistics: {
          total_deliveries: 1,
          total_quantity: 10000,
          total_cost: 170000,
          average_cost_per_liter: 17.00,
          suppliers: ['BP Energy'],
          first_delivery_date: '2024-08-25T10:00:00Z',
          last_delivery_date: '2024-08-25T10:00:00Z',
          delivery_frequency_days: 0
        },
        api_readings: [],
        latest_api_reading: null,
        api_reading_count: 0,
        refueling_records: [],
        refueling_statistics: {
          total_refuels: 0,
          total_quantity: 0,
          total_cost: 0,
          average_cost_per_liter: 0,
          unique_vehicles: 0,
          unique_drivers: 0,
          card_refuels: 0,
          cash_refuels: 0,
          fuel_types: [],
          fuel_consumption: {
            average_km_per_liter: 0,
            average_liters_per_100km: 0,
            total_distance: 0,
            efficiency_rating: 'N/A'
          },
          date_range: { from: '', to: '' }
        },
        fuel_consumption: {
          average_km_per_liter: 0,
          average_liters_per_100km: 0,
          total_distance: 0,
          efficiency_rating: 'N/A'
        }
      }
    ]

    // Filter mock data based on filters
    let filteredData = mockTankData

    if (filters.tankIds && filters.tankIds.length > 0) {
      filteredData = filteredData.filter(tank => filters.tankIds.includes(tank.tank_id))
    }

    // Apply variance threshold filter
    if (filters.varianceThreshold !== undefined) {
      filteredData = filteredData.map(tank => ({
        ...tank,
        status: tank.variance_percentage <= filters.varianceThreshold ? 'acceptable' : 'investigate'
      }))
    }

    return {
      data: filteredData,
      summary: this.calculateSummary(filteredData, filters)
    }
  }

  async getTankOptions(): Promise<any[]> {
    try {
      const authStore = useAuthStore()
      const currentUserRole = authStore.user?.user_metadata?.role || ''
      const isHeadOffice = authStore.hasGlobalAccess

      let currentOfficeIds: string[] = []
      if (!isHeadOffice) {
        let currentUserCompany = authStore.user?.user_metadata?.company || ''
        if (!currentUserCompany && authStore.user?.id) {
            const { data: profile } = await supabase.from('user_profiles').select('company').eq('user_id', authStore.user.id).single()
            if (profile && profile.company) currentUserCompany = profile.company
        }
        currentOfficeIds = currentUserCompany ? currentUserCompany.split(',').map((id: string) => id.trim()).filter(Boolean) : []
        if (currentOfficeIds.length === 0) return []
      }

      let query = supabase
        .from('fuel_tanks')
        .select('id, name, depot_name, capacity, fuel_type')
        .eq('status', 'active')
        .order('name')

      if (!isHeadOffice) {
        query = query.in('office_id', currentOfficeIds)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching tanks:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error connecting to database:', error)
      return []
    }
  }

  async getOfficeOptions(): Promise<any[]> {
    return [
      { id: 'office-001', name: 'Main Depot' },
      { id: 'office-002', name: 'North Depot' }
    ]
  }

  async saveReconciliation(reconciliationData: any) {
    // Mock save - in real implementation this would save to database
    console.log('Saving reconciliation:', reconciliationData)
    return { success: true }
  }

  /**
   * Get daily tank levels at 6:00 AM and 18:00 (6:00 PM) for each day in the date range
   */
  async getDailyTankLevels(filters: { dateFrom: string; dateTo: string; tankIds?: string[] }): Promise<any[]> {
    try {
      console.log('📊 Fetching daily tank levels:', filters)

      // Get all active tanks or filtered tanks
      let tankQuery = supabase
        .from('fuel_tanks')
        .select('id, name, fuel_type, depot_name, capacity')
        .eq('status', 'active')
        .order('name')

      if (filters.tankIds && filters.tankIds.length > 0) {
        tankQuery = tankQuery.in('id', filters.tankIds)
      }

      const { data: tanks, error: tankError } = await tankQuery

      if (tankError) {
        console.error('❌ Error fetching tanks:', tankError)
        return []
      }

      if (!tanks || tanks.length === 0) {
        return []
      }

      // Generate array of dates between dateFrom and dateTo
      const startDate = new Date(filters.dateFrom)
      const endDate = new Date(filters.dateTo)
      const dates: Date[] = []

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d))
      }

      console.log(`📅 Processing ${dates.length} days for ${tanks.length} tanks`)

      // For each tank, get readings at 6:00 AM and 18:00 for each day
      const dailyLevels: any[] = []

      for (const tank of tanks) {
        for (const date of dates) {
          const dateStr = date.toISOString().split('T')[0]

          // Get reading closest to 6:00 AM
          const morning6am = `${dateStr}T06:00:00`
          const { data: morning6Reading } = await supabase
            .from('fuel_tank_api_readings')
            .select('*')
            .eq('tank_id', tank.id)
            .gte('reading_timestamp', morning6am)
            .lte('reading_timestamp', `${dateStr}T08:00:00`) // Within 2 hours of 6 AM
            .order('reading_timestamp', { ascending: true })
            .limit(1)

          // Get reading closest to 18:00 (6:00 PM)
          const evening6pm = `${dateStr}T18:00:00`
          const { data: evening6Reading } = await supabase
            .from('fuel_tank_api_readings')
            .select('*')
            .eq('tank_id', tank.id)
            .gte('reading_timestamp', evening6pm)
            .lte('reading_timestamp', `${dateStr}T20:00:00`) // Within 2 hours of 6 PM
            .order('reading_timestamp', { ascending: true })
            .limit(1)

          dailyLevels.push({
            date: dateStr,
            tank_id: tank.id,
            tank_name: tank.name,
            depot_name: tank.depot_name,
            fuel_type: tank.fuel_type,
            capacity: tank.capacity,
            level_6am: morning6Reading?.[0]?.volume_litres || null,
            level_6am_timestamp: morning6Reading?.[0]?.reading_timestamp || null,
            level_18h: evening6Reading?.[0]?.volume_litres || null,
            level_18h_timestamp: evening6Reading?.[0]?.reading_timestamp || null,
            variance: morning6Reading?.[0] && evening6Reading?.[0]
              ? (evening6Reading[0].volume_litres - morning6Reading[0].volume_litres)
              : null
          })
        }
      }

      console.log(`✅ Generated ${dailyLevels.length} daily level records`)
      return dailyLevels

    } catch (error) {
      console.error('❌ Error fetching daily tank levels:', error)
      return []
    }
  }
}

export const tankReconciliationService = new TankReconciliationService()
